<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainAnalyticsAPI from 'dashboard/api/captain/analytics';
import MetricCard from './components/MetricCard.vue';
import SentimentChart from './components/SentimentChart.vue';
import TimeseriesChart from './components/TimeseriesChart.vue';
import TopicsChart from './components/TopicsChart.vue';
import InsightsTable from './components/InsightsTable.vue';
import PendingStats from './components/PendingStats.vue';
import InboxSettings from './components/InboxSettings.vue';

const store = useStore();
const { t } = useI18n();

const summary = ref({});
const timeseries = ref([]);
const topics = ref({});
const pendingStats = ref({});
const analyticsInboxes = ref([]);
const inboxesKey = ref(0);
const isLoading = ref(false);
const isRunningAnalysis = ref(false);
const showSettings = ref(false);

const filters = ref({
  inbox_id: null,
  start_date: null,
  end_date: null,
});

const analysisOptions = ref({
  include_resolved: false,
  min_hours_open: 0,
});

const inboxes = computed(() => store.getters['inboxes/getInboxes']);

const formatTime = seconds => {
  if (!seconds) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
};

const fetchData = async () => {
  isLoading.value = true;
  try {
    const params = {
      inboxId: filters.value.inbox_id,
      startDate: filters.value.start_date,
      endDate: filters.value.end_date,
    };

    const [summaryRes, timeseriesRes, topicsRes, pendingRes] =
      await Promise.all([
        CaptainAnalyticsAPI.getSummary(params),
        CaptainAnalyticsAPI.getTimeseries(params),
        CaptainAnalyticsAPI.getTopics(params),
        CaptainAnalyticsAPI.getPendingStats(params),
      ]);

    summary.value = summaryRes.data;
    timeseries.value = timeseriesRes.data;
    topics.value = topicsRes.data;
    pendingStats.value = pendingRes.data;
  } catch {
    // Error handled silently
  } finally {
    isLoading.value = false;
  }
};

const fetchInboxes = async () => {
  try {
    const response = await CaptainAnalyticsAPI.getInboxes();
    analyticsInboxes.value = response.data;
  } catch {
    // Error handled silently
  }
};

const handleInboxChange = event => {
  filters.value.inbox_id = event.target.value || null;
};

const handleDateChange = (type, event) => {
  filters.value[type] = event.target.value || null;
};

const runAnalysis = async () => {
  isRunningAnalysis.value = true;
  try {
    await CaptainAnalyticsAPI.runAnalysis({
      inboxId: filters.value.inbox_id,
      includeResolved: analysisOptions.value.include_resolved,
      minHoursOpen: analysisOptions.value.min_hours_open,
    });
    useAlert(t('CAPTAIN_ANALYTICS.ANALYSIS.STARTED'));
  } catch {
    useAlert(t('CAPTAIN_ANALYTICS.ANALYSIS.ERROR'));
  } finally {
    isRunningAnalysis.value = false;
  }
};

const toggleInbox = async (inboxId, enabled) => {
  const inboxIndex = analyticsInboxes.value.findIndex(i => i.id === inboxId);
  if (inboxIndex === -1) return;

  // Create new array with updated inbox to ensure Vue reactivity
  analyticsInboxes.value = analyticsInboxes.value.map((inbox, idx) =>
    idx === inboxIndex
      ? { ...inbox, captain_analytics_enabled: enabled }
      : inbox
  );
  inboxesKey.value += 1; // Force re-render

  try {
    await CaptainAnalyticsAPI.updateInbox(inboxId, enabled);
    useAlert(t('CAPTAIN_ANALYTICS.SETTINGS.UPDATED'));
  } catch (error) {
    // Revert the optimistic update on error
    analyticsInboxes.value = analyticsInboxes.value.map((inbox, idx) =>
      idx === inboxIndex
        ? { ...inbox, captain_analytics_enabled: !enabled }
        : inbox
    );
    inboxesKey.value += 1; // Force re-render on revert
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      t('CAPTAIN_ANALYTICS.SETTINGS.ERROR');
    useAlert(errorMessage);
  }
};

watch(filters, fetchData, { deep: true });

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchData();
  fetchInboxes();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background p-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-n-slate-12">
        {{ $t('CAPTAIN_ANALYTICS.TITLE') }}
      </h1>
      <div class="flex gap-2">
        <button
          class="px-4 py-2 rounded-lg bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 transition-colors flex items-center gap-2"
          @click="showSettings = !showSettings"
        >
          <span class="i-lucide-settings text-lg" />
          {{ $t('CAPTAIN_ANALYTICS.SETTINGS.TITLE') }}
        </button>
        <button
          class="px-4 py-2 rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors flex items-center gap-2 disabled:opacity-50"
          :disabled="isRunningAnalysis"
          @click="runAnalysis"
        >
          <span
            :class="
              isRunningAnalysis
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-play'
            "
            class="text-lg"
          />
          {{ $t('CAPTAIN_ANALYTICS.ANALYSIS.RUN') }}
        </button>
      </div>
    </div>

    <!-- Settings Panel -->
    <InboxSettings
      v-if="showSettings"
      :key="inboxesKey"
      :inboxes="analyticsInboxes"
      :analysis-options="analysisOptions"
      @toggle="toggleInbox"
      @update:analysis-options="analysisOptions = $event"
      @close="showSettings = false"
    />

    <div class="flex flex-wrap gap-4 mb-6">
      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-11">
          {{ $t('CAPTAIN_ANALYTICS.FILTERS.INBOX') }}
        </label>
        <select
          class="px-3 py-2 rounded-lg border border-n-container bg-n-solid-2 text-n-slate-12 min-w-[200px]"
          :value="filters.inbox_id"
          @change="handleInboxChange"
        >
          <option value="">
            {{ $t('CAPTAIN_ANALYTICS.FILTERS.ALL_INBOXES') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-11">
          {{ $t('CAPTAIN_ANALYTICS.FILTERS.START_DATE') }}
        </label>
        <input
          type="date"
          class="px-3 py-2 rounded-lg border border-n-container bg-n-solid-2 text-n-slate-12"
          :value="filters.start_date"
          @change="event => handleDateChange('start_date', event)"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-11">
          {{ $t('CAPTAIN_ANALYTICS.FILTERS.END_DATE') }}
        </label>
        <input
          type="date"
          class="px-3 py-2 rounded-lg border border-n-container bg-n-solid-2 text-n-slate-12"
          :value="filters.end_date"
          @change="event => handleDateChange('end_date', event)"
        />
      </div>
    </div>

    <!-- Pending Stats -->
    <PendingStats :data="pendingStats" :is-loading="isLoading" class="mb-6" />

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <MetricCard
        :title="$t('CAPTAIN_ANALYTICS.METRICS.TOTAL_CONVERSATIONS')"
        :value="summary.total_conversations || 0"
        :is-loading="isLoading"
      />
      <MetricCard
        :title="$t('CAPTAIN_ANALYTICS.METRICS.QUALIFIED_LEADS')"
        :value="summary.qualified_leads || 0"
        type="success"
        :is-loading="isLoading"
      />
      <MetricCard
        :title="$t('CAPTAIN_ANALYTICS.METRICS.AVG_RESPONSE_TIME')"
        :value="formatTime(summary.avg_first_response_time)"
        :is-loading="isLoading"
      />
      <MetricCard
        :title="$t('CAPTAIN_ANALYTICS.METRICS.OFFENSIVE_INCIDENTS')"
        :value="summary.offensive_incidents || 0"
        :type="summary.offensive_incidents > 0 ? 'warning' : 'default'"
        :is-loading="isLoading"
      />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <SentimentChart
        :data="summary.sentiment_distribution || {}"
        :is-loading="isLoading"
      />
      <TopicsChart :data="topics" :is-loading="isLoading" />
    </div>

    <div class="mb-6">
      <TimeseriesChart :data="timeseries" :is-loading="isLoading" />
    </div>

    <div>
      <InsightsTable :filters="filters" />
    </div>
  </div>
</template>
